import { createElement, useEffect, useState } from 'react';
import { Layers, Bot, ChevronLeft, ChevronRight, ExternalLink, ArrowUp, Mail, Linkedin, Github, Eye, Quote, CircleOff, Phone, MapPin, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button, Modal, Tabs, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  FaHtml5, FaCss3Alt, FaJs, FaDatabase, FaBootstrap, FaReact, FaAngular, FaWordpress,
  FaTrello, FaGitlab, FaGithub, FaLinkedin, FaJira, FaMicrosoft, FaFigma, FaPalette, FaPencilRuler,
  FaServer, FaShoppingCart
} from 'react-icons/fa';
import { SiSpring, SiTailwindcss, SiNextdotjs, SiNotion, SiCanvas } from 'react-icons/si';

const Icon = ({ icon: IconComponent, ...props }: { icon: any; [key: string]: any }) => createElement(IconComponent, props);

type ProcessStep = {
  title: string;
  description: string;
};



const skillCategories = [
  {
    title: 'Frontend',
    skills: [
      { name: 'HTML', icon: FaHtml5, color: '#e34c26', delay: '0s' },
      { name: 'CSS', icon: FaCss3Alt, color: '#264de4', delay: '0.5s' },
      { name: 'JavaScript', icon: FaJs, color: '#f0db4f', delay: '1s' },
      { name: 'React', icon: FaReact, color: '#61dafb', delay: '1.5s' },
      { name: 'Angular', icon: FaAngular, color: '#dd0031', delay: '2s' },
      { name: 'Next.js', icon: SiNextdotjs, color: '#1e293b', delay: '2.5s' },
      { name: 'Bootstrap', icon: FaBootstrap, color: '#7952b3', delay: '3s' },
      { name: 'Tailwind CSS', icon: SiTailwindcss, color: '#06b6d4', delay: '3.5s' },
    ]
  },
  {
    title: 'Backend & CMS',
    skills: [
      { name: 'SQL', icon: FaDatabase, color: '#336791', delay: '0s' },
      { name: 'Oracle PL/SQL', icon: FaDatabase, color: '#c74634', delay: '0.5s' },
      { name: 'API REST', icon: FaServer, color: '#7c3aed', delay: '1s' },
      { name: 'Spring Boot', icon: SiSpring, color: '#6db33f', delay: '1.5s' },
      { name: 'WordPress', icon: FaWordpress, color: '#21759b', delay: '2s' },
      { name: 'Magento', icon: FaShoppingCart, color: '#f26322', delay: '2.5s' },
    ]
  },
  {
    title: 'Outils & Collaboration',
    skills: [
      { name: 'GitHub', icon: FaGithub, color: '#333', delay: '0s' },
      { name: 'GitLab', icon: FaGitlab, color: '#fc6d26', delay: '0.5s' },
      { name: 'Jira', icon: FaJira, color: '#0052cc', delay: '1s' },
      { name: 'Trello', icon: FaTrello, color: '#0079bf', delay: '1.5s' },
      { name: 'Notion', icon: SiNotion, color: '#1e293b', delay: '2s' },
      { name: 'Teams', icon: FaMicrosoft, color: '#6264a7', delay: '2.5s' },
      { name: 'Microsoft Office', icon: FaMicrosoft, color: '#d83b01', delay: '3s' },
    ]
  },
  {
    title: 'Design & Prototypage',
    skills: [
      { name: 'Figma', icon: FaFigma, color: '#f24e1e', delay: '0s' },
      { name: 'Canva', icon: SiCanvas, color: '#00c4cc', delay: '0.5s' },
      { name: 'Webdesign', icon: FaPalette, color: '#7c3aed', delay: '1s' },
      { name: 'Prototypage', icon: FaPencilRuler, color: '#7c3aed', delay: '1.5s' },
    ]
  },
];

function Home({ grayscaleMode, onToggleGrayscale }: { grayscaleMode: boolean; onToggleGrayscale: () => void }) {
  const { t } = useTranslation();
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [selectedProjectIndex, setSelectedProjectIndex] = useState<number | null>(null);

  useEffect(() => {
    const updateBackToTopVisibility = () => {
      setShowBackToTop(window.scrollY >= 600);
    };

    updateBackToTopVisibility();
    window.addEventListener('scroll', updateBackToTopVisibility, { passive: true });
    return () => window.removeEventListener('scroll', updateBackToTopVisibility);
  }, []);

  const recentProjects = [

    {
      number: "3",
      title: "Application d'arbre généalogique (Familia)",
      description: "Application web pour trouver l'arbre généalogique de sa famille. Les fonctionnalités clées se résument en la création et la recherche des membres d'une famille, la consultation du patrimoine économique et social d'une famille.",
      product: "Familia centralise l'histoire, les liens et le patrimoine d'une famille dans un espace numérique simple à consulter et à enrichir.",
      operation: "L'utilisateur crée un espace familial ou recherche un arbre existant, puis ajoute chaque membre en renseignant nom, date de naissance, filiation et photo. Il visualise l'arbre généalogique interactif, explore les fiches individuelles et accède aux indicateurs socio-économiques du patrimoine familial. Un système de rôles permet de gérer les droits de modification, tandis que le moteur de recherche facilite la découverte de liens entre différentes familles.",
      imageUrl: "/image3.png",
      tags: ["Angular", "SpringBoot", "Java", "MySQL"],
      software: ["VS Code", "IntelliJ IDEA", "Postman", "MySQL Workbench", "Figma", "Git"],
      size: "normal",
      href: "https://web-red-beta-86.vercel.app/"
    },
    {
      number: "1",
      title: "Application de gestion des dépenses",
      description: "Application de suivi des dépenses personnelles développée en ReactJS avec l'API Chrome pour le stockage local et les notifications.",
      product: "Cette application aide à suivre ses dépenses personnelles au quotidien et à mieux visualiser l'utilisation de son budget.",
      operation: "L'utilisateur ajoute ses revenus et ses dépenses par catégorie, fixe des budgets mensuels et consulte des graphiques de répartition en temps réel. L'application enregistre les données en local via l'API Chrome storage, affiche des rappels par notifications natives et génère des rapports récapitulatifs. Aucune authentification ni serveur distant n'est requis, ce qui garantit la confidentialité et la rapidité d'exécution.",
      imageUrl: "/image2.png",
      tags: ["ReactJS", "Chrome API", "JavaScript"],
      software: ["VS Code", "Chrome DevTools", "Figma", "Git"],
      size: "wide",
      href: "https://gestion-depenses-gold.vercel.app/"
    },
    {
      number: "2",
      title: "Application d'événement festive",
      description: "Plateforme pour la gestion et l'organisation d'événements festifs. Objectif aux nouvelles personnes venues dans une ville ou zone de créer du reseau et de tuer l'ennui.",
      product: "Cette plateforme facilite la découverte et l'organisation d'événements festifs afin de créer du lien entre les habitants d'une même zone.",
      operation: "Les organisateurs créent un événement en renseignant la date, le lieu, la capacité et le type d'animation. Les participants parcourent la carte des événements, filtrent par catégorie ou distance, s'inscrivent et reçoivent une confirmation. Le tableau de bord de l'organisateur centralise les inscriptions, les messages et les statistiques de participation, tandis qu'un système de notifications rappelle les événements à venir.",
      imageUrl: "/image1.png",
      tags: ["Angular", "Django", "TypeScript"],
      software: ["VS Code", "PyCharm", "Postman", "Figma", "Git"],
      size: "tall",
      href: "#"
    },
    {
      number: "4",
      title: "AI Web Agency",
      description: "Opportunités commerciales – Prospects : une relation commerciale liée à une Company, avec pagination backend.",
      product: "AI Web Agency est une console d'opérations qui aide une agence web à générer des opportunités commerciales : recherche de prospects, scoring qualité, filtrage avancé et lancement de propositions.",
      operation: "Rechercher de nouveaux prospects : choisissez une activité et une ville. La recherche est exécutée en arrière-plan. Filtrer les prospects enregistrés : affinez la liste ci-dessous sans lancer de nouvelle recherche. Chaque fiche affiche le site généré, l'entreprise, la catégorie, la ville, le pays, le score, la qualité du site, le site d'origine, le statut (DISCOVERED), la dernière analyse et la prochaine action à mener ('Analyser et qualifier cette opportunité'). Sélectionnez les prospects puis cliquez sur 'Lancer les propositions' pour déclencher les propositions commerciales.",
      imageUrl: "/image4.png",
      tags: ["React", "TypeScript", "AI", "SaaS"],
      software: ["VS Code", "GitHub", "Figma", "Vercel", "Git"],
      size: "wide",
      href: "https://github.com/fertners/agency-web"
    },


  ];

  const academicGroupProjects = [
    {
      title: "Age of knowledge",
      description: "Application web du jeu de societe Ludo, avec possiblité de jouer avec un bot.",
      imageUrl: "/6.png",
      tags: ["JavaScript, Angular"],
      size: "normal",
      href: "https://web-red-beta-86.vercel.app/"
    },
   
  ];

  const professionalProjects = [

    {
      title: "Koudbat",
      description: "Outil de gestion des équipes, des disponibilités et du calendrier des interventions.",
      imageUrl: "/5.png",
      tags: ["React", "TypeScript", "Figma"],
      size: "wide",
      href: "https://www.koudbat.fr/"
    },
 
    {
      title: "Suite Inawo",
      description:"Application de gestion de la relation clients, permettant de centraliser les informations sur les clients et les prospects. Elle facilite le suivi des interactions, la gestion des opportunités commerciales et l'amélioration de la relation client.",
      imageUrl: "/2.png",
      tags: ["ReactJS", "JavaScript", "Tailwind CSS"],
      size: "tall",
      href: "https://inawo.pro/en/"
    },
    {
      title: "Suite Dispatch",
      description: "Application web permettant d'améliorer la productivité des chantiers et d'accompagner dans une gestion optimisée et durable des ressources : personnel, matériel et matériaux.",
      imageUrl: "/3.png",
      tags: ["ReactJS", "TypeScript", "Django"],
      size: "tall",
      href: "https://www.suitedispatch.com/fr/"
    },

    {
      title: "Grolot",
      description: "Application web destinée aux professionnels du bâtiment, notamment les artisans, PME, TPE et bureaux d’études. Elle vise à simplifier la gestion des appels d’offres en automatisant l’analyse des dossiers de consultation des entreprises, la recherche de marchés, la réalisation des métrés et la préparation des réponses administratives et techniques.",
      imageUrl: "/4.png",
      tags: ["Next.js", "TypeScript", "Django"],
      size: "wide",
      href: "https://www.grolot.fr/"
    },

  ];

  const personalProjectProcess: ProcessStep[] = [
    { title: "Idée & besoin", description: "Définition du problème, des fonctionnalités utiles et des utilisateurs ciblés." },
    { title: "Maquettes", description: "Conception de l'interface et validation du parcours utilisateur." },
    { title: "Développement", description: "Création du frontend, intégration des données et tests des fonctionnalités." },
    { title: "Mise en ligne", description: "Optimisation, déploiement et suivi des améliorations." }
  ];

  const professionalProjectProcess: ProcessStep[] = [
    { title: "Cadrage", description: "Analyse du besoin métier et définition des objectifs avec l'équipe." },
    { title: "Conception", description: "Réalisation des maquettes et préparation des composants à développer." },
    { title: "Collaboration", description: "Développement en lien avec les équipes produit, backend et QA." },
    { title: "Validation", description: "Tests, retours utilisateurs et mise à disposition de la fonctionnalité." }
  ];

  const academicProjectProcess: ProcessStep[] = [
    { title: "Analyse", description: "Compréhension du sujet, répartition des rôles et choix des technologies." },
    { title: "Travail d'équipe", description: "Organisation des tâches, échanges réguliers et suivi de l'avancement." },
    { title: "Réalisation", description: "Développement, intégration et tests de la solution." },
    { title: "Présentation", description: "Préparation de la démonstration et restitution du projet." }
  ];

  const recommendations = [
    {
      name: "Kolawole Mangabo",
      role: "Senior Software Engineer",
      date: "Décembre 2024",
      initials: "KM",
      text: "Néfertiti is a fast learner and meticulous developer. Her attention to detail and autonomy are the top qualities that made me enjoy working with her. She actively seeks feedback, enthusiastically implements it, and constantly strives to improve her craft. Her ability to communicate clearly and her willingness to share knowledge has made her an integral part of the team. She is a talented developer and a supportive and reliable colleague. I’m excited to see all the incredible things she will accomplish in her career."
    },
    {
      name: "Djroton Noé SOSSOU",
      role: "Technical Lead at Quantibat",
      date: "Novembre 2024",
      initials: "DS",
      text: "J’ai eu l’occasion de travailler avec Néfertiti Dadjo et j’ai apprécié son sérieux et son implication dans chaque projet. Elle est fiable, organisée et s’adapte bien aux différentes situations. C’est un plaisir de collaborer avec elle, et je la recommande pour son professionnalisme."
    }
  ];

  return (
    <div id="top" className="pb-16">
      {/* Hero Section */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center justify-items-center mb-24 lg:mb-28 min-h-auto py-20">
        <div className="flex flex-col items-start gap-5 max-w-xl">
          
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-800 leading-tight">
            Hello, <span className="bg-gradient-to-r from-primary to-purple-400 bg-clip-text text-transparent">Bienvenue</span> 
            {/* <span className="inline-block animate-wave">👋</span> */}
          </h1>
          <p className="text-lg md:text-xl text-slate-600 font-medium">{t('home.role')}</p>
          <p className="text-base text-slate-500 leading-relaxed">
            {t('home.intro')}
          </p>
          <div className="hero-info">
            <a href="tel:+33605608536" className="hero-info-item">
              <Phone size={16} aria-hidden="true" />
              <span>{t('home.phoneNumber')}</span>
            </a>
            <div className="hero-info-item">
              <MapPin size={16} aria-hidden="true" />
              <span>{t('home.location')}</span>
            </div>
            <div className="hero-info-item">
              <Briefcase size={16} aria-hidden="true" />
              <span> 02 années </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <a href="/nefertiti-dadjo-cv.pdf" download className="px-6 py-3 bg-primary text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition-colors">{t('home.downloadCv')}</a>
            <Link to="/contact" className="px-6 py-3 bg-white text-slate-700 font-medium rounded-lg border border-slate-200 hover:border-primary hover:text-primary transition-colors">{t('home.contactMe')}</Link>
          </div>
          <div className="flex items-center gap-3">
            <a href="https://github.com/quantibat" target="_blank" rel="noopener noreferrer" aria-label="GitHub" className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600 hover:bg-primary hover:text-white transition-colors">
              <Icon icon={FaGithub} size={20} />
            </a>
            <a href="https://www.linkedin.com/in/nefertiti-dadjo/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600 hover:bg-primary hover:text-white transition-colors">
              <Icon icon={FaLinkedin} size={20} />
            </a>
          </div>
        </div>
        <div className="relative w-full h-full">
          <div className=" w-full md:w-80 md:h-80 lg:w-full lg:h-auto ">
            <img src="/nefertiti.png" alt="Photo de profil de Néfertiti" className="w-[1000px] object-cover" />
          </div>
          <div className="hero-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-10"></div>
        </div>
      </section>

      {/* Recent Projects Section */}
      <section id="projets" className="recent-projects mb-20 lg:mb-24">
          <div className="projects-header">
            <div className='flex flex-col gap-2'>
              <h2 className='text-5xl sm:text-4xl lg:text-5xl font-bold'>{t('home.discover')}</h2>
              <h3 className='text-xl sm:text-lg lg:text-xl text-gray-500'>{t('home.personalProjects')}</h3>
            </div>
          </div>

        <div className="projects-masonry-wrapper">
          <div className="projects-masonry">
            {recentProjects.map((project, index) => (
              <article key={index} className="masonry-card">
                <div className={`masonry-card-visual ${project.size}`}>
                  <img src={project.imageUrl} alt={project.title} />
                </div>
                <div className="project-card-body">
                  <h3>{project.title}</h3>
                  <div className="project-tags">
                    {project.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="tag">{tag}</span>
                    ))}
                  </div>
                  <p>{project.description}</p>
                  <div className="flex flex-wrap gap-2">
                    <Button type="default" icon={<Eye size={16} />} onClick={() => setSelectedProjectIndex(index)} className="w-fit bg-white/5 hover:bg-transparent focus:bg-transparent text-xs text-slate-800">
                      {t('home.viewDetails')}
                    </Button>
                    {project.href !== "#" && (
                      <Button type="default" href={project.href} target="_blank" icon={<ExternalLink size={16} />} className="w-fit bg-white/5 hover:bg-transparent focus:bg-transparent text-xs text-slate-800">
                        {t('home.viewProject')}
                      </Button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
        {selectedProjectIndex !== null && (
          <Modal
            open
            title={recentProjects[selectedProjectIndex].title}
            onCancel={() => setSelectedProjectIndex(null)}
            footer={null}
            centered
            width={720}
          >
            <img
              src={recentProjects[selectedProjectIndex].imageUrl}
              alt={recentProjects[selectedProjectIndex].title}
              className="w-full max-h-72 object-cover rounded-lg mb-6"
            />
            <Tabs
              defaultActiveKey="product"
              items={[
                {
                  key: 'product',
                  label: t('home.product'),
                  children: (
                    <div className="p-4 bg-white rounded-lg border border-slate-100 shadow-sm">
                      <p className="text-slate-600 leading-7">{recentProjects[selectedProjectIndex].product}</p>
                    </div>
                  )
                },
                {
                  key: 'operation',
                  label: t('home.operation'),
                  children: (
                    <div className="p-4 bg-white rounded-lg border border-slate-100 shadow-sm">
                      <p className="text-slate-600 leading-7">{recentProjects[selectedProjectIndex].operation}</p>
                    </div>
                  )
                },
                {
                  key: 'technologies',
                  label: t('home.technologies'),
                  children: (
                    <div className="p-4 bg-white rounded-lg border border-slate-100 shadow-sm">
                      <div className="project-tags">
                        {recentProjects[selectedProjectIndex].tags.map((tag) => <span key={tag} className="tag">{tag}</span>)}
                      </div>
                    </div>
                  )
                },
                {
                  key: 'software',
                  label: 'Logiciels & outils',
                  children: (
                    <div className="p-4 bg-white rounded-lg border border-slate-100 shadow-sm">
                      <div className="project-tags">
                        {recentProjects[selectedProjectIndex].software.map((soft) => <span key={soft} className="tag">{soft}</span>)}
                      </div>
                    </div>
                  )
                }
              ]}
            />
          </Modal>
        )}
      </section>


      {/* Recent Projects Section professionneles */}
      <section id="projets-pro" className="recent-projects mb-20 lg:mb-24">
        <div className="projects-header">
          <div className='flex flex-col gap-2'>
            <h2 className='text-5xl sm:text-4xl lg:text-5xl font-bold'>{t('home.discover')}</h2>
            <h3 className='text-xl sm:text-lg lg:text-xl text-gray-500'>{t('home.professionalProjects')}</h3>
          </div>
        </div>

        <div className="projects-masonry-wrapper">
          <div className="projects-masonry">
            {professionalProjects.map((project, index) => (
              <Link to="#" key={index} className="masonry-card">
                <div className={`masonry-card-visual ${project.size}`}>
                  <img src={project.imageUrl} alt={project.title} />
                </div>
                <div className="project-card-body">
                  <h3>{project.title}</h3>
                  <div className="project-tags">
                    {project.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="tag">{tag}</span>
                    ))}
                  </div>
                  <p>{project.description}</p>
                  <Button type="default" icon={<Eye size={16} />} className="w-fit bg-white/5 hover:bg-transparent focus:bg-transparent text-xs text-slate-800">
                    {t('home.viewProject')}
                  </Button>
                </div>
              </Link>
            ))}
          </div>

        </div>
      </section>

      {/* Academic Projects Section */}
      <section id="projets-academiques" className="recent-projects mb-20 lg:mb-24">
        <div className="projects-header">
          <div className='flex flex-col gap-2'>
            <h2 className='text-5xl sm:text-4xl lg:text-5xl font-bold'>{t('home.discover')}</h2>
            <h3 className='text-xl sm:text-lg lg:text-xl text-gray-500'>{t('home.academicProjects')}</h3>
          </div>
        </div>

        <div className="projects-masonry-wrapper">
          <div className="projects-masonry">
            {academicGroupProjects.map((project, index) => (
              <Link to={project.href || "#"} key={index} className="masonry-card">
                <div className={`masonry-card-visual ${project.size}`}>
                  <img src={project.imageUrl} alt={project.title} />
                </div>
                <div className="project-card-body">
                  <h3>{project.title}</h3>
                  <div className="project-tags">
                    {project.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="tag">{tag}</span>
                    ))}
                  </div>
                  <p>{project.description}</p>
                  <Button type="default" icon={<Eye size={16} />} className="w-fit bg-white/5 hover:bg-transparent focus:bg-transparent text-xs text-slate-800">
                    {t('home.viewProject')}
                    {project.href && <ExternalLink size={16} />}
                  </Button>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="services" className="mb-20 lg:mb-24">
        <h2 className="text-4xl md:text-5xl font-bold text-slate-800 ">{t('home.skills')}</h2>
        <h3 className="text-xl sm:text-lg lg:text-xl text-gray-500 mt-4 mb-10">{t('home.skillsSubtitle')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {skillCategories.map((category, categoryIndex) => (
            <div className="bg-white/5 rounded-2xl p-6 shadow-sm border border-slate-200" key={categoryIndex}>
              <h3 className="text-base font-bold text-slate-800 mb-5 pb-3 border-b-2 border-slate-200">{category.title}</h3>
              <div className="flex flex-wrap gap-3">
                {category.skills.map((skill, skillIndex) => (
                  <div className="flex flex-col items-center gap-2 p-3 bg-slate-50 rounded-xl shadow-sm hover:shadow-md hover:bg-white transition-all duration-300 animate-float min-w-[80px]" key={skillIndex} style={{ animationDelay: skill.delay, color: skill.color }}>
                    <Icon icon={skill.icon} size={28} />
                    <span className="text-xs font-medium text-slate-600 text-center">{skill.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Professional Experience Timeline */}
      <section id="experiences" className="mb-16 lg:mb-20">
        <h2 className="text-4xl md:text-5xl font-bold text-slate-800 ">Mes expériences</h2>
        <h3 className="text-lg text-slate-600 mb-8 mt-4">{t('home.experienceSubtitle')}</h3>
        <div className="timeline">
          <div className="timeline-item">
            <div className="timeline-date-side">Mars 2025 - Juin 2026</div>
            <div className="timeline-dot"></div>
            <div className="timeline-content">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="text-lg font-semibold text-slate-800">Apprenti Développeur Frontend</h3>
                <Tag color="purple">QUANTIBAT, France</Tag>
              </div>
              <ul className="timeline-missions space-y-1 text-sm text-slate-500 leading-relaxed">
                <li>Conception et développement d'interfaces utilisateurs responsives avec Next.js et Tailwind CSS.</li>
                <li>Création de composants réutilisables (filtres, formulaires, outils) pour la Suite Dispatch.</li>
                <li>Création de maquettes et prototypes interactifs avec Figma.</li>
                <li>Manipulation des données avec une API RESTful.</li>
                <li>Collaboration avec l'équipe de développement et de QA.</li>
                <li>Gestion de projets et suivi des échéances pour la livraison des fonctionnalités.</li>
                <li>Veille technologique régulière et adaptation aux évolutions du marché.</li>
              </ul>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-date-side">Déc. 2023 - Août 2024</div>
            <div className="timeline-dot"></div>
            <div className="timeline-content">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="text-lg font-semibold text-slate-800">Développeur Frontend</h3>
                <Tag color="purple">FASFOX, France</Tag>
              </div>
              <ul className="timeline-missions space-y-1 text-sm text-slate-500 leading-relaxed">
                <li>Conception et développement d'interfaces utilisateurs responsives avec Next.js et Tailwind CSS.</li>
                <li>Création de composants réutilisables pour la Suite Dispatch.</li>
                <li>Manipulation des données avec une API RESTful.</li>
                <li>Collaboration avec l'équipe de développement et de QA.</li>
              </ul>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-date-side">Août 2022 - Sept. 2024</div>
            <div className="timeline-dot"></div>
            <div className="timeline-content">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="text-lg font-semibold text-slate-800">Développeur Frontend</h3>
                <Tag color="purple">INAWO TECH, Bénin</Tag>
              </div>
              <ul className="timeline-missions space-y-1 text-sm text-slate-500 leading-relaxed">
                <li>Conception et implémentation de la Suite Inawo, application de gestion de la relation client.</li>
                <li>Implémentation de composants réutilisables avec ReactJS.</li>
                <li>Développement des fonctionnalités (Catalogue, Agenda, Dépenses, Experts, Innovations, Publicités).</li>
                <li>Conception de sites web sous WordPress.</li>
                <li>Création de sites web en HTML, Bootstrap, Tailwind CSS et JavaScript.</li>
                <li>Optimisation de contenus web pour améliorer l'expérience utilisateur.</li>
                <li>Rédaction de la documentation technique de support.</li>
              </ul>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-date-side">Sept. 2021 - Janv. 2022</div>
            <div className="timeline-dot"></div>
            <div className="timeline-content">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="text-lg font-semibold text-slate-800">Testeur Informatique</h3>
                <Tag color="purple">FA CONSULTING, Bénin</Tag>
              </div>
              <ul className="timeline-missions space-y-1 text-sm text-slate-500 leading-relaxed">
                <li>Formation sur l'utilisation des applications de gestion des utilisateurs.</li>
                <li>Rédaction de la documentation technique de support.</li>
                <li>Test et QA des fonctionnalités (méthodologie Agile).</li>
                <li>Maîtrise des principes de l'assurance qualité et de la méthode Agile.</li>
              </ul>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-date-side">Oct. 2020 - Août 2021</div>
            <div className="timeline-dot"></div>
            <div className="timeline-content">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="text-lg font-semibold text-slate-800">Assistante en Informatique</h3>
                <Tag color="purple">TOP FORMATION, Bénin</Tag>
              </div>
              <ul className="timeline-missions space-y-1 text-sm text-slate-500 leading-relaxed">
                <li>Accueil et réception des clients et support informatique.</li>
                <li>Analyse des besoins et recommandations en solutions informatiques.</li>
                <li>Formation des utilisateurs sur les outils informatiques.</li>
                <li>Formation en Microsoft Office (Word et Excel).</li>
                <li>Création de supports de cours de formation.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="recommandations" className="mb-16 lg:mb-20">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 text-center">On a travaillé ensemble</h2>
        <h3 className="text-lg text-slate-600 mb-8 mt-4 text-center">{t('home.recommendationsSubtitle')}</h3>
        <div className="recommendations-grid">
          {recommendations.map((recommendation) => (
            <article className="recommendation-card timeline-content" key={recommendation.name}>
              <Quote className="recommendation-quote" size={28} aria-hidden="true" />
              <blockquote>{recommendation.text}</blockquote>
             <div className="flex gap-10 justify-between items-end ">
               <div className="recommendation-author">
                <span className="recommendation-avatar">{recommendation.initials}</span>
                <div>
                  <h3>{recommendation.name}</h3>
                  <p>{recommendation.role}</p>
                  <span>{recommendation.date}</span>
                </div>
              </div>
              <Button  href='https://www.linkedin.com/in/nefertiti-dadjo/'  icon={<ExternalLink size={16} />} className="w-fit bg-white/5 hover:bg-transparent focus:bg-transparent text-xs text-slate-800"/>
             </div>
            </article>
          ))}
        </div>
      </section>

      {/* Academic Experience Timeline */}
      <section className="mb-16 lg:mb-20">
        <h2 className="text-4xl md:text-5xl font-bold text-slate-800 ">{t('home.education')}</h2>
        <h3 className="text-lg text-slate-600 mb-8 mt-4">{t('home.educationSubtitle')}</h3>
        <div className="timeline">
          <div className="timeline-item">
            <div className="timeline-date-side">2025 - 2028</div>
            <div className="timeline-dot"></div>
            <div className="timeline-content">
              <div className='education-logos'>
                  <img src="/urca.png" alt="Logo URCA" className="education-logo" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">Diplôme Ingénieur Systèmes d'information</h3>
              <p className="timeline-company text-sm font-medium text-primary mb-3">CNAM Grand Est, Reims (France)</p>
              <p className="timeline-description text-sm text-slate-500 leading-relaxed">Formation d'ingénieur en systèmes d'information.</p>
            </div>
          </div>
          <div className="timeline-item">
            <div className="timeline-date-side">2024 - 2025</div>
            <div className="timeline-dot"></div>
            <div className="timeline-content">
              <div className="education-logos">
                <img src="/eisine.jpg" alt="Logo Eisine" className="education-logo" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">Licence professionnelle en Webdesign et Intégration web</h3>
              <p className="timeline-company text-sm font-medium text-primary mb-3">Eisine URCA, Reims (France)</p>
              <p className="timeline-description text-sm text-slate-500 leading-relaxed">Spécialisation en webdesign et intégration web.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact mb-16 lg:mb-20">
        <h2 className="contact-title">{t('home.contactTitle')}</h2>
     
        <p className="contact-subtitle ">
          {t('home.contactSubtitle')}
        </p>

        

        <div className="contact-cards mb-10">
          <a href="mailto:ghislainedadjo@gmail.com" className="contact-card">
            <div className="contact-icon" >
              <Mail size={20} />
            </div>
            <span className="contact-text">ghislainedadjo@gmail.com</span>
          </a>
          <a href="https://www.linkedin.com/in/nefertiti-dadjo/" target="_blank" rel="noopener noreferrer" className="contact-card">
            <div className="contact-icon" >
              <Linkedin size={20} />
            </div>
            <span className="contact-text">linkedin.com/in/nefertiti-dadjo</span>
          </a>
          <a href="https://github.com/quantibat" target="_blank" rel="noopener noreferrer" className="contact-card">
            <div className="contact-icon" >
              <Github size={20} />
            </div>
            <span className="contact-text">github.com/quantibat</span>
          </a>
        </div>

        {/* <div className="contact-video">
          <iframe
            src="https://www.youtube-nocookie.com/embed/geU7XHCg0Lk"
            title="Démonstration de portfolio développeur"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div> */}
      </section>



      {showBackToTop && (
        <div className="page-controls">
          <button
            type="button"
            className="grayscale-mode-toggle"
            onClick={onToggleGrayscale}
            aria-pressed={grayscaleMode}
            aria-label={t('home.grayscaleLabel')}
          >
            <CircleOff size={20} />
          </button>
          <a href="#top" className="back-to-top" aria-label={t('home.backToTop')}>
            <ArrowUp size={20} />
          </a>
        </div>
      )}
    </div>
  );
}

export default Home;
